import { AccountOnboarding } from '@adtrackify/at-tracking-event-types';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import {
  OnboardingLadderStepId,
} from '@/features/setup/types/onboarding-ladder-enums';
import { useAuthenticatedMutation } from '@/hooks/auth/use-auth-query-hooks';
import { applyOnboardingToAccountsCache } from '@/features/setup/helpers/onboarding-cache-update';
import { useAccountState } from '@/contexts/account/account-context';
import { AccountsApiService } from '@/services/internal-api/accounts/accounts-api-service';
import type { AccountOnboardingResponse } from '@/services/internal-api/accounts/types/accounts-api-service-types';

interface UpdateTimestampVariables {
  stepId: OnboardingLadderStepId;
  timestamp: string;
}

function buildTimestampPatch(
  stepId: OnboardingLadderStepId,
  timestamp: string
): Partial<AccountOnboarding> {
  switch (stepId) {
    case OnboardingLadderStepId.CONNECT_SHOPIFY:
      return { dataSourceConnectedAt: timestamp };
    case OnboardingLadderStepId.CONNECT_FACEBOOK_SOURCE:
      return { dataSources: { facebookAdsConnectedAt: timestamp } };
    case OnboardingLadderStepId.CONNECT_GOOGLE_SOURCE:
      return { dataSources: { googleAdsConnectedAt: timestamp } };
    case OnboardingLadderStepId.ENABLE_FACEBOOK_DESTINATION:
      return { destinations: { metaCapiConnectedAt: timestamp } };
    case OnboardingLadderStepId.CONNECT_KLAVIYO:
      return { destinations: { klaviyoConnectedAt: timestamp } };
    case OnboardingLadderStepId.ENABLE_GOOGLE_DESTINATION:
      return { destinations: { googleAdsCapiConnectedAt: timestamp } };
    default:
      return {};
  }
}

export function hasTimestampForStep(
  onboarding: AccountOnboarding,
  stepId: OnboardingLadderStepId
): boolean {
  switch (stepId) {
    case OnboardingLadderStepId.CONNECT_SHOPIFY:
      return !!onboarding.dataSourceConnectedAt;
    case OnboardingLadderStepId.CONNECT_FACEBOOK_SOURCE:
      return !!onboarding.dataSources?.facebookAdsConnectedAt;
    case OnboardingLadderStepId.CONNECT_GOOGLE_SOURCE:
      return !!onboarding.dataSources?.googleAdsConnectedAt;
    case OnboardingLadderStepId.ENABLE_FACEBOOK_DESTINATION:
      return !!onboarding.destinations?.metaCapiConnectedAt;
    case OnboardingLadderStepId.CONNECT_KLAVIYO:
      return !!onboarding.destinations?.klaviyoConnectedAt;
    case OnboardingLadderStepId.ENABLE_GOOGLE_DESTINATION:
      return !!onboarding.destinations?.googleAdsCapiConnectedAt;
    default:
      return false;
  }
}

export function useUpdateDerivedStepTimestamp() {
  const queryClient = useQueryClient();
  const { accountId } = useAccountState();

  return useAuthenticatedMutation<
    AxiosResponse<AccountOnboardingResponse>,
    unknown,
    UpdateTimestampVariables
  >({
    mutationFn: ({ stepId, timestamp }) => {
      if (!accountId) {
        throw new Error('Cannot update derived step timestamp without an account');
      }
      return AccountsApiService.updateAccountOnboarding(accountId, {
        onboarding: buildTimestampPatch(stepId, timestamp),
      });
    },

    onSuccess: (response) => {
      applyOnboardingToAccountsCache(queryClient, accountId ?? '', response.data.onboarding);
    },

    onError: (error) => {
      console.warn('Failed to persist derived step timestamp', error);
    },
  });
}
